import { z } from "zod";

const text = z.string().trim().min(1);
const color = z.string().regex(/^#[0-9a-f]{6}$/i);
export const componentFrameSchema = z.object({
  x: z.number().min(0).max(13.334), y: z.number().min(0).max(7.5),
  w: z.number().positive().max(13.334), h: z.number().min(0).max(7.5),
}).strict();
export const componentAppearanceSchema = z.object({
  fill: color.optional(), color: color.optional(), lineColor: color.optional(),
  lineWidth: z.number().min(0).max(8).optional(),
  transparency: z.number().min(0).max(100).optional(),
  fontSize: z.number().min(10).max(96).optional(), bold: z.boolean().optional(),
  align: z.enum(["left", "center", "right"]).optional(),
}).strict();
export const designedLayoutSchema = z.enum([
  "cover", "timeline", "line_chart", "organization", "comparison",
  "closing", "statement", "image_split",
  "matrix", "columns", "table", "flow",
]);
export const chartPlanSchema = z.object({
  type: z.enum(["line", "bar", "doughnut"]).optional(),
  labels: z.array(text.max(40)).min(2).max(12),
  series: z.array(z.object({
    name: text.max(60), values: z.array(z.number().finite()).min(2).max(12),
  }).strict()).min(1).max(3),
  sourceLabel: text.max(160), simulated: z.boolean(),
}).strict().superRefine((chart, context) => {
  if (chart.type === "doughnut" && (chart.series.length !== 1 || chart.series[0]!.values.some((value) => value < 0))) {
    context.addIssue({ code: "custom", message: "Doughnut charts require one nonnegative series." });
  }
  if (chart.series.some((series) => series.values.length !== chart.labels.length)) {
    context.addIssue({ code: "custom", message: "Every chart value must have one label." });
  }
});
export const tablePlanSchema = z.object({
  headers: z.array(text.max(40)).min(2).max(7),
  rows: z.array(z.array(text.max(100)).min(2).max(7)).min(1).max(8),
  sourceLabel: text.max(160),
}).strict().superRefine((table, context) => {
  if (table.rows.some((row) => row.length !== table.headers.length)) context.addIssue({ code: "custom", message: "Table columns must match headers." });
});
const step = z.object({
  label: text.max(80), detail: text.max(120), period: text.max(40).optional(),
}).strict();
const comparisonSide = z.object({
  heading: text.max(40), items: z.array(text.max(90)).min(1).max(6),
}).strict();
export const slideDesignSchema = z.object({
  layout: designedLayoutSchema,
  title: text.max(120),
  subtitle: text.max(180).optional(),
  eyebrow: text.max(80).optional(),
  points: z.array(text.max(180)).max(6).default([]),
  imagePrompt: text.max(1400).optional(),
  chart: chartPlanSchema.optional(),
  table: tablePlanSchema.optional(),
  matrix: z.object({ xLabel: text.max(40), yLabel: text.max(40), cells: z.array(z.object({ label: text.max(40), items: z.array(text.max(80)).min(1).max(4) }).strict()).length(4) }).strict().optional(),
  columns: z.array(z.object({ heading: text.max(40), items: z.array(text.max(100)).min(1).max(5) }).strict()).min(2).max(8).optional(),
  timeline: z.array(step).min(2).max(7).optional(),
  organization: z.object({
    root: text.max(60), children: z.array(text.max(60)).min(2).max(6),
    evolution: z.array(step).min(2).max(4).optional(),
  }).strict().optional(),
  comparison: z.object({
    left: comparisonSide, right: comparisonSide,
    bridge: text.max(60), caption: text.max(140).optional(),
  }).strict().optional(),
  requiredTexts: z.array(text.max(180)).max(64).default([]),
}).strict().superRefine((design, context) => {
  const required = { timeline: "timeline", flow: "timeline", line_chart: "chart", organization: "organization", comparison: "comparison", table: "table", matrix: "matrix", columns: "columns" } as const;
  const field = required[design.layout as keyof typeof required];
  if (design.imagePrompt && !["cover", "closing", "image_split", "comparison"].includes(design.layout)) context.addIssue({ code: "custom", message: "Artwork is only supported on cover, closing, image_split or comparison." });
  if (design.layout === "cover" && design.points.length > 2) context.addIssue({ code: "custom", message: "Cover supports at most two concise supporting lines." });
  if (design.layout === "comparison" && design.points.length) context.addIssue({ code: "custom", message: "Use comparison fields, not points, on comparison slides." });
  if (["matrix", "columns", "table"].includes(design.layout) && design.points.length) context.addIssue({ code: "custom", message: "Use native structured fields, not points, for " + design.layout });
  const supportedStructuredFields = {
    chart: ["line_chart"],
    timeline: ["timeline", "flow", "closing"],
    organization: ["organization"],
    comparison: ["comparison"],
    table: ["table"], matrix: ["matrix"], columns: ["columns"],
  } as const;
  for (const [structuredField, layouts] of Object.entries(supportedStructuredFields)) {
    const key = structuredField as keyof typeof supportedStructuredFields;
    if (design[key] && !(layouts as readonly string[]).includes(design.layout)) {
      context.addIssue({ code: "custom", message: structuredField + " is not rendered by " + design.layout + "." });
    }
  }
  if (field && !design[field]) context.addIssue({ code: "custom", message: design.layout + " requires structured " + field });
  if (["cover", "closing", "image_split"].includes(design.layout) && !design.imagePrompt) {
    context.addIssue({ code: "custom", message: design.layout + " requires a planned image." });
  }
});
export type SlideDesign = z.infer<typeof slideDesignSchema>;
export type ChartPlan = z.infer<typeof chartPlanSchema>;
export type ComponentFrame = z.infer<typeof componentFrameSchema>;
export type ComponentAppearance = z.infer<typeof componentAppearanceSchema>;
